import { useEffect, useRef, RefObject } from 'react';

/**
 * Hook a11y minimalista para modales legacy.
 *
 * Hace SOLO 2 cosas:
 *   1. Lock del scroll del <body> mientras el modal está abierto.
 *   2. ESC para cerrar (con stopPropagation para no afectar otros listeners).
 *
 * NO hace focus restore ni focus trap — esa lógica antes estaba acá pero
 * causaba pérdida de foco después de cada keystroke (el cleanup llamaba a
 * `previouslyFocused.current?.focus()` cuando cualquier prop del padre
 * cambiaba, sacándole el foco al input que el usuario estaba editando).
 *
 * El focus inicial al abrir lo maneja el usuario (puede agregar `autoFocus`
 * al primer input). El Tab natural del browser navega entre elementos
 * focuseables. Si en el futuro queremos focus trap real, migrar al
 * componente `<Modal />` que ya lo tiene implementado correctamente.
 */
export function useDialogA11y(
  isOpen: boolean,
  onClose: () => void,
  _containerRef: RefObject<HTMLElement>,
) {
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', handleKey);

    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);
}
